import React, { useState, useEffect, useMemo, useCallback } from "react";
import cn from "classnames";
import styles from "./DocumentsAndFiles.module.sass";
import Card from "../../../components/Card";
import Control from "./Control";
import { showSuccess, showError, showInfo } from "../../../services/toast.service";
import { useAuth } from "../../../contexts/AuthContext";

// Importation des services réels
import { getStudentDocuments, uploadDocument, submitDocuments } from "../../../services/documents.service";
import { pb } from "../../../services/pocketbase";
import Icon from "../../../components/Icon";

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const PDF_EXTENSION = 'pdf';
const WORD_EXTENSIONS = ['doc', 'docx'];
const EXCEL_EXTENSIONS = ['xls', 'xlsx'];

// Taille maximale autorisée : 10 Mo
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Utilisation des icônes réelles du système
const FILE_ICONS = {
  pdf: '/images/content/file-pdf.svg',
  doc: '/images/content/file-doc.svg',
  xls: '/images/content/file-xls.svg',
  default: '/images/content/file-document.svg',
  upload: '/images/content/upload.svg'
};

// Définition des documents justificatifs requis
const REQUIRED_DOCUMENTS = [
  { type: 'Carte identité', label: 'Carte Nationale d\'Identité' },
  { type: 'Passport', label: 'Passeport' },
  { type: 'Permis', label: 'Permis de Conduire' },
  { type: 'RIB', label: 'Relevé d\'Identité Bancaire' },
  { type: 'Carte vitale', label: 'Carte Vitale' },
  { type: 'Justificatif de domicile', label: 'Justificatif de Domicile' },
  { type: 'Dernier diplome', label: 'Dernier Diplôme' },
  { type: 'Curriculum vitae', label: 'Curriculum Vitae' },
  { type: 'Attestation RQTH', label: 'Attestation RQTH' },
  { type: 'Attestation de recensement', label: 'Attestation de Recensement' },
  { type: 'Diplôme', label: 'Diplôme' },
  { type: 'Autre', label: 'Autre' }
];

const DocumentsAndFiles = ({ className, studentId }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDocumentsCount, setPendingDocumentsCount] = useState(0);

  // Function to fetch documents (centralized for reusability)
  const fetchDocuments = useCallback(async () => {
    if (!studentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await getStudentDocuments(studentId);
      setDocuments(result.items);
      const pendingCount = result.items.filter(doc => doc.statut === 'pending').length;
      setPendingDocumentsCount(pendingCount);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      showError("Erreur lors du chargement des documents."); // Error toast for loading
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  // Initial fetch on component mount and studentId change
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Function to get file URL
  const getFileUrl = useCallback((document) => {
    if (!document || !document.file) return null;
    return `${pb.baseUrl}/api/files/student_documents/${document.id}/${document.file}`;
  }, []);

  // Function to determine file type (image, pdf, other)
  const getFileType = useCallback((fileName) => {
    if (!fileName) return 'unknown';
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (IMAGE_EXTENSIONS.includes(ext)) {
      return 'image';
    } else if (ext === PDF_EXTENSION) {
      return 'pdf';
    } else {
      return 'other';
    }
  }, []);

  // Function to get appropriate file icon
  const getFileIcon = useCallback((fileName) => {
    if (!fileName) return FILE_ICONS.default;
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (ext === PDF_EXTENSION) {
      return FILE_ICONS.pdf;
    } else if (WORD_EXTENSIONS.includes(ext)) {
      return FILE_ICONS.doc;
    } else if (EXCEL_EXTENSIONS.includes(ext)) {
      return FILE_ICONS.xls;
    } else {
      return FILE_ICONS.default;
    }
  }, []);

  // Function to render document preview (image, PDF, or generic icon)
  const renderPreview = useCallback((document) => {
    const fileUrl = getFileUrl(document);
    if (!fileUrl) {
      return <img src={FILE_ICONS.default} alt="Document sans fichier" />;
    }

    const fileType = getFileType(document.file);

    if (fileType === 'image') {
      return (
        <div className={styles.imagePreview}>
          <img src={fileUrl} alt={document.type || 'Image'} />
        </div>
      );
    } else if (fileType === 'pdf') {
      return (
        <div className={styles.pdfPreview}>
          <iframe
            src={`${fileUrl}#page=1&view=FitH&zoom=75`}
            title={document.type || 'PDF document'}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          >
            <img src={getFileIcon(document.file)} alt="PDF document" />
          </iframe>
        </div>
      );
    } else {
      return <img src={getFileIcon(document.file)} alt={document.type || 'Document'} />;
    }
  }, [getFileUrl, getFileType, getFileIcon]);

  // Status badge component to display document status
  const StatusBadge = ({ status }) => {
    // Define status classes and labels using existing status classes
    const statusConfig = {
      ok: { className: "status-green-dark", label: 'ok' },
      ko: { className: "status-red", label: 'ko' },
      pending: { className: "status-yellow", label: 'en attente' },
      submitted: { className: "status-blue", label: 'soumis' }
    };

    const config = statusConfig[status] || { className: "status-gray", label: status };

    return (
      <div className={cn(styles.status, config.className)}>
        {config.label}
      </div>
    );
  };

  // Memoized component for an existing document item
  const DocumentItem = React.memo(({ document }) => {
    return (
      <div
        key={document.id}
        className={cn(styles.product, {})}
      >
        <div className={styles.preview}>
          <Control className={styles.control} document={document} onDocumentChange={fetchDocuments} />
          {renderPreview(document)}
        </div>
        <div className={styles.line}>
          <div className={styles.title}>{document.type || 'Document sans type'}</div>
        </div>
        <div className={styles.dateStatusLine}>
          <div className={styles.date}>
            {new Date(document.created).toLocaleDateString('fr-FR')}
          </div>
          <StatusBadge status={document.statut} />
        </div>
      </div>
    );
  });

  // File component for drag-and-drop and file selection
  const File = ({ className, title, label, onFileDrop }) => {
    const [uploadedFileName, setUploadedFileName] = useState(null);

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Vérifier la taille du fichier
        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
          showError(`Le fichier est trop volumineux (${sizeMB} Mo). Taille maximale autorisée : 10 Mo`);
          e.target.value = ''; // Reset l'input
          return;
        }
        setUploadedFileName(file.name);
        if (onFileDrop) {
          onFileDrop(file);
        }
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      if (file) {
        // Vérifier la taille du fichier
        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
          showError(`Le fichier est trop volumineux (${sizeMB} Mo). Taille maximale autorisée : 10 Mo`);
          return;
        }
        setUploadedFileName(file.name);
        if (onFileDrop) {
          onFileDrop(file);
        }
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <div
        className={cn(className, styles.fileUploadArea)}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          type="file"
          id={`file-upload-${label.replace(/\s/g, '-')}`}
          className={styles.fileInput}
          onChange={handleFileChange}
        />
        <label htmlFor={`file-upload-${label.replace(/\s/g, '-')}`} className={styles.fileLabelContent}>
          <Icon name="upload" />
          <p className={styles.uploadTitle}>{label}</p>
          {uploadedFileName ? (
            <p className={styles.uploadedFileName}>Fichier: {uploadedFileName}</p>
          ) : (
            <p className={styles.uploadPlaceholder}>{title}</p>
          )}
        </label>
      </div>
    );
  };

  // File upload handler (NO SUCCESS TOAST HERE)
  const handleFileUpload = useCallback(async (documentType, file) => {
    if (!file || !studentId) {
      console.error('Fichier ou studentId manquant');
      return;
    }

    try {
      // Récupérer le partner_id depuis l'utilisateur connecté
      const partnerId = user?.partner_id;
      
      const result = await uploadDocument(studentId, file, documentType, partnerId);

      if (result) {
        console.log('Document uploadé avec succès:', result);
        // showSuccess(`Le document "${documentType}" a été uploadé avec succès !`); // Removed this line
        fetchDocuments(); // Refresh the list
      }
    } catch (error) {
      console.error(`Erreur lors de l'upload du document ${documentType}:`, error);
      showError(`Erreur lors de l'upload du document "${documentType}".`); // Error toast for upload
    }
  }, [studentId, user?.partner_id, fetchDocuments]);

  // Function to submit all pending documents (SUCCESS TOAST HERE)
  const handleSubmitDocuments = useCallback(async () => {
    if (pendingDocumentsCount === 0) {
      console.log('Aucun document en attente à soumettre');
      showInfo('Aucun document en attente à soumettre.'); // Info toast
      return;
    }

    setSubmitting(true);
    try {
      const success = await submitDocuments(studentId);
      if (success) {
        showSuccess(`Tous les ${pendingDocumentsCount} document${pendingDocumentsCount > 1 ? 's' : ''} ont été soumis avec succès !`); // Success toast for submission
        fetchDocuments();
        setPendingDocumentsCount(0); // Reset pending count after successful submission
      }
    } catch (error) {
      console.error('Erreur lors de la soumission des documents:', error);
      showError('Erreur lors de la soumission des documents.'); // Error toast for submission
    } finally {
      setSubmitting(false);
    }
  }, [studentId, pendingDocumentsCount, fetchDocuments]);


  return (
    <>
      <Card className={cn(styles.card, className)} title="Documents élèves" classTitle="title-blue"
        head={
          <>
            {pendingDocumentsCount > 0 && (
              <div className={styles.submitContainer}>
                <button
                  className={cn(styles.submitButton, { [styles.submitting]: submitting })}
                  onClick={handleSubmitDocuments}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>Soumission en cours...</>
                  ) : (
                    <>
                      <Icon name="upload" size="20" />
                      Soumettre {pendingDocumentsCount} document{pendingDocumentsCount > 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        }
      >
        {loading ? (
          <div className={styles.loading}>Chargement des documents...</div>
        ) : (
          <>
            <div className={styles.products}>
              {REQUIRED_DOCUMENTS.map((requiredDoc) => {
                const foundDocument = documents.find(
                  (doc) => doc.type === requiredDoc.type
                );

                if (foundDocument) {
                  return <DocumentItem key={foundDocument.id} document={foundDocument} />;
                } else {
                  return (
                    <div key={requiredDoc.type} className={cn(styles.product, styles.missingDocument)}>
                      <File
                        className={styles.field}
                        title="Cliquez ou déposez un fichier"
                        label={requiredDoc.label}
                        onFileDrop={(file) => handleFileUpload(requiredDoc.type, file)}
                      />
                    </div>
                  );
                }
              })}
            </div>
          </>
        )}
      </Card>
    </>
  );
};

export default DocumentsAndFiles;